import React from 'react'
import { sdk } from '@/lib/sdk'
import type { ConsentTexts } from '@/lib/sdk-official/resources/sms'

/* F-142: what the signup, the login and the SMS screens share. */

const textsCache = new Map<string, Promise<ConsentTexts | null>>()

/**
 * The current consent texts of this community (public route), loaded once per
 * community and language. `null` while loading or when the route fails: the
 * forms then go on without the text and send no consent.
 */
export const useConsentTexts = (communityId: string | undefined, language: string): ConsentTexts | null => {
  const [texts, setTexts] = React.useState<ConsentTexts | null>(null)
  React.useEffect(() => {
    if (!communityId) return
    const key = `${communityId}:${language}`
    if (!textsCache.has(key)) {
      textsCache.set(key, sdk.sms.consentTexts().catch(() => {
        textsCache.delete(key)
        return null
      }))
    }
    let active = true
    void textsCache.get(key)!.then((value) => { if (active) setTexts(value) })
    return () => { active = false }
  }, [communityId, language])
  return texts
}

/**
 * What the single per-channel toggle sends on: the version of the channel's
 * combined notices-and-news text (`texts.<channel>.all`), so the API records
 * both consents under the text the person saw. Nothing extra with an API
 * that has no such text yet: `marketing: true` alongside still records both.
 */
export const allConsentOf = (texts: ConsentTexts | null, channel: 'sms' | 'whatsapp'): { textVersion?: string } => {
  const all = texts?.[channel]?.all
  return all?.version ? { textVersion: all.version } : {}
}

/**
 * 2026-10-28: the one consent signup and login ask, an unchecked box with the
 * API's text (`texts.signup`: SMS and WhatsApp together, or the only channel
 * offered). Checked, its version goes as `smsConsent.textVersion` and the API
 * records notices and news of each channel it covers. `null`: nothing to ask.
 */
export const signupConsentOf = (texts: ConsentTexts | null | undefined) => texts?.signup ?? null

/** The version the login remembers having shown (once per community and text). */
export const loginConsentVersion = (texts: ConsentTexts | null | undefined): string | undefined => signupConsentOf(texts)?.version

/**
 * The login shows the consent text once per community (F-142, question 4):
 * after a login in this browser that carried it, it is not shown again here.
 * A cleared browser shows it once more, which records nothing new when the
 * person already consented to the current version.
 */
const seenKey = (communityId: string, version: string) => `memberfy_sms_consent:${communityId}:${version}`

export const loginConsentSeen = (communityId: string | undefined, version: string | undefined): boolean => {
  if (!communityId || !version) return true
  try {
    return window.localStorage.getItem(seenKey(communityId, version)) === '1'
  } catch {
    return false
  }
}

export const markLoginConsentSeen = (communityId: string | undefined, version: string | undefined) => {
  if (!communityId || !version) return
  try {
    window.localStorage.setItem(seenKey(communityId, version), '1')
  } catch {
    /* Private window: shown again next time, which is harmless. */
  }
}

interface ApiError {
  response?: { status?: number; data?: { message?: string; errors?: { param?: string; message?: string }[]; data?: { retryAfterSeconds?: number } } }
  retryAfter?: number
}

export const errorStatus = (error: unknown): number | undefined => (error as ApiError | null)?.response?.status

export const errorParam = (error: unknown): string | undefined => (error as ApiError | null)?.response?.data?.errors?.[0]?.param

/** Seconds a 429 asks to wait, from the body or the header. */
export const retryAfterOf = (error: unknown): number | null => {
  const e = error as ApiError | null
  const seconds = e?.response?.data?.data?.retryAfterSeconds ?? e?.retryAfter
  return typeof seconds === 'number' && seconds > 0 ? seconds : null
}

/** A countdown in whole seconds; `start(n)` restarts it. */
export const useCountdown = () => {
  const [left, setLeft] = React.useState(0)
  React.useEffect(() => {
    if (left <= 0) return
    const id = window.setTimeout(() => setLeft((n) => Math.max(0, n - 1)), 1000)
    return () => window.clearTimeout(id)
  }, [left])
  return { left, start: setLeft }
}

/** "27/10" in the reader's language and the community's zone (`useCommunityTime().zone`). */
export const shortDate = (iso: string, language: string, timeZone?: string): string =>
  new Intl.DateTimeFormat(language, { day: '2-digit', month: '2-digit', timeZone }).format(new Date(iso))
